import React from 'react';
import heroImage from '../assets/article-antibiotic-stewardship.png';
import articleBodyImage from '../assets/article-antibiotic1.png';
import articleClinicalImage from '../assets/article-antibiotic2.png';

const ArticleAntibioticStewardshipOutpatient = () => {
  return (
    <div className="bg-white pb-16">
      <section
        className="relative isolate min-h-[38vh] w-full overflow-hidden sm:min-h-[40vh]"
        aria-labelledby="abx-article-hero-heading"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/55 via-slate-950/35 to-slate-950/60"
          aria-hidden
        />
        <div className="relative z-10 mx-auto flex min-h-[38vh] max-w-6xl flex-col items-center justify-center px-4 py-12 sm:min-h-[40vh] sm:px-6 sm:py-14 lg:px-8">
          <div className="w-full max-w-3xl text-center">
            <h1
              id="abx-article-hero-heading"
              className="text-pretty text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              Antibiotic stewardship in outpatient settings
            </h1>
            <p className="mt-4 text-sm text-slate-100/95 sm:text-base">
              <span className="text-slate-100">By Vikram Desai, PharmD</span>
              <span className="mx-2 text-slate-300/90" aria-hidden>
                ·
              </span>
              <time dateTime="2026-04-28" className="tabular-nums text-slate-100/90">
                April 28, 2026
              </time>
            </p>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12"
        aria-labelledby="abx-article-body-heading"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] md:items-stretch md:gap-8 lg:gap-10">
          <div className="min-h-0 w-full md:h-full">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 md:aspect-auto md:h-full md:min-h-0">
              <img
                src={articleBodyImage}
                alt="Outpatient care flat lay: stethoscope, pill organiser, blister packs, thermometer, and dressings on a neutral surface"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          <div className="min-h-0 min-w-0 space-y-5 text-base leading-relaxed text-slate-600">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">Evidence and scope</p>
              <h2
                id="abx-article-body-heading"
                className="mt-2 text-pretty text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"
              >
                Why outpatient stewardship matters
              </h2>
            </div>
            <p>
              Outpatient prescribing drives a large share of antibiotic use. Stewardship programmes designed for
              hospitals do not always translate to clinics, urgent care, and telehealth visits.
            </p>
            <p>
              Here we outline practical levers—formularies, delayed prescriptions, diagnostic stewardship, and patient
              education—and what the evidence suggests about their effect on prescribing and resistance.
            </p>
            <p>
              Narrow-spectrum choices, dose and duration that match the working diagnosis, and clear documentation of
              why an antibiotic was started all help later decisions if symptoms change or cultures return. Small
              defaults in the record (for example, stop dates and indication fields) can nudge safer patterns without
              slowing front-line care.
            </p>
            <p>
              Measuring outpatient stewardship is harder than on wards: claims and pharmacy data lag, and viral
              syndromes look similar early on. Still, tracking high-volume indications, repeat courses, and broad-spectrum
              use by site can show where feedback, protocols, or point-of-care tests are likely to pay off.
            </p>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12"
        aria-labelledby="abx-article-practice-heading"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] md:items-stretch md:gap-8 lg:gap-10">
          <div className="min-h-0 min-w-0 space-y-5 text-base leading-relaxed text-slate-600">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">Operational practice</p>
              <h2
                id="abx-article-practice-heading"
                className="mt-2 text-pretty text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"
              >
                Turning levers into day-to-day practice
              </h2>
            </div>
            <p>
              Delayed prescribing strategies, clear after-visit instructions about when symptoms should trigger a call,
              and paired communication between pharmacy and primary care can reduce unnecessary starts without leaving
              patients unsure what to do overnight.
            </p>
            <p>
              Where rapid tests are available, tying them to default order sets helps clinicians act on a negative
              result in the moment rather than days later. Where they are not, structured revisit windows and safety-net
              advice remain the backbone of sensible watchful waiting.
            </p>
            <p>
              Stewardship is not only “fewer scripts”; it is safer care when antibiotics are needed—right drug, dose,
              duration, and follow-up—so resistance pressure and adverse events both move in the right direction.
            </p>
          </div>
          <div className="min-h-0 w-full md:h-full">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 md:aspect-auto md:h-full md:min-h-0">
              <img
                src={articleClinicalImage}
                alt="Blister packs and assorted capsules and tablets on a white surface, illustrating outpatient antibiotic use"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleAntibioticStewardshipOutpatient;
